"use strict";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Button,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  ButtonGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
  IconButton,
  SimpleGrid,
  useBreakpointValue,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { 
  FaCheckCircle, 
  FaClock,
  FaPaperPlane,
  FaMagic,
  FaInbox,
  FaCalendarAlt,
  FaRegCalendarCheck,
  FaRegClock,
  FaRegBell,
  FaRegEnvelope,
  FaChevronDown,
  FaArrowRight,
  FaCommentDots,
} from "react-icons/fa";
import { MdInbox } from "react-icons/md";
import { FiFilter } from "react-icons/fi";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaSearch } from "react-icons/fa";
import { keyframes } from "@emotion/react";

// Add a visual transition animation for the whole panel
const panelExitAnimation = keyframes`
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-100%); }
`;

// Add a more pronounced animation for going back to the inbox
const panelReverseExitAnimation = keyframes`
  0% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(100%); }
`;

// Add a panel enter animation keyframes for smoother transitions
const panelReverseEnterAnimation = keyframes`
  0% { opacity: 0; transform: translateX(-100%); }
  30% { opacity: 0.5; transform: translateX(-50px); }
  100% { opacity: 1; transform: translateX(0); }
`;

// Pulse animation for the click affordance
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;
const pulseAnimation = `${pulse} 2s infinite`;

// Let's also add a visual indicator to show which tab was clicked
const activeTabIndicatorAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

// Define a specific back button animation
const backButtonClickAnimation = keyframes`
  0% { transform: scale(1); background-color: #3182ce; }
  50% { transform: scale(0.95); background-color: #2b6cb0; }
  100% { transform: scale(1); background-color: #3182ce; }
`;

// Feature card component similar to FeatureCard in LandingDealSection
const FeatureCard = ({ title, description, icon, color }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  return (
    <Box
      p={isMobile ? 3 : 6}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      bg={bgColor}
      height="100%"
      transition="all 0.2s"
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: "lg",
        borderColor: `${color}.300`,
      }}
      _active={{
        animation: pulseAnimation,
      }}
    >
      <Flex 
        width={isMobile ? "40px" : "60px"}
        height={isMobile ? "40px" : "60px"}
        borderRadius="lg"
        bg={`${color}.100`}
        color={`${color}.500`}
        justifyContent="center"
        alignItems="center"
        mb={isMobile ? 2 : 4}
      >
        <Icon as={icon} boxSize={isMobile ? 4 : 6} />
      </Flex>
      
      <Text 
        fontSize={isMobile ? "md" : "xl"} 
        fontWeight="bold" 
        mb={isMobile ? 2 : 3}
        color={textColor}
      >
        {title}
      </Text>
      
      <Text fontSize={isMobile ? "xs" : "md"} color={secondaryTextColor}>
        {description}
      </Text>
    </Box>
  );
};

// Email Response Suggestion component based on EmailFollowupDetail.jsx
const EmailResponseSuggestion = ({ selectedTask }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const tagBg = useColorModeValue("blue.50", "blue.800");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  
  // Add state for tracking visibility
  const [isVisible, setIsVisible] = useState(false);
  const responseRef = useRef(null);

  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (responseRef.current) {
      observer.observe(responseRef.current);
    }
    
    return () => {
      if (responseRef.current) {
        observer.unobserve(responseRef.current);
      }
    };
  }, []);
  
  // State for subject line and sender
  const [emailSubject, setEmailSubject] = useState("");
  const [selectedSender, setSelectedSender] = useState({
    name: "Ricky Roma",
    email: "ricky@lysto.ai"
  });
  const senderOptions = [
    { name: "Ricky Roma", email: "ricky@lysto.ai" },
    { name: "Shelley Levene", email: "shelley@lysto.ai" },
    { name: "Dave Moss", email: "dave@lysto.ai" },
    { name: "George Aaronow", email: "george@lysto.ai" },
    { name: "John Williamson", email: "john@lysto.ai" },
    { name: "James Lingk", email: "james@lysto.ai" },
    { name: "Blake", email: "blake@lysto.ai" },
    { name: "Detective Baylen", email: "detective@lysto.ai" }
  ];
  
  // Default to first task if none selected
  const task = selectedTask || {
    id: 6,
    type: "Reply",
    task_type: "Email",
    task_subtype: "Initial",
    task: "Respond to Coinbase partnership inquiry",
    deal: "Coinbase",
    recipients: "B",
    lastActivity: "2 weeks ago",
    isUnread: false,
    priority: "low",
    status: "to do",
    owner: "Ricky Roma"
  };
  
  // Set subject based on task when task changes
  React.useEffect(() => {
    if (task) {
      setEmailSubject(`Re: ${task.task}`);
    }
  }, [task]);
  
  // Generate recipients from task data
  const getRecipients = () => {
    // In a real app, this would parse the recipients field
    // For the demo, we'll create sample recipients based on the deal name
    return [
      { name: task.deal, email: `contact@${task.deal.toLowerCase().replace(/\s+/g, '')}.com` }
    ];
  };
  
  // Get task details based on type
  const getEmailContext = () => {
    // Create custom email context based on task
    switch(task.deal) {
      case "Airbnb":
        return "We've been evaluating several solutions for our sales team. Your platform looks promising, but I need more details on enterprise pricing. Can you send over a custom quote for 500+ seats?";
      case "Coinbase":
        return "Thanks for the demo last week. I'm intrigued by your platform's capabilities. We're exploring several options for improving our sales processes. What kind of integration options do you offer for crypto payment platforms?";
      case "Stripe":
        return "Our integration team was impressed with your demo. We need to understand how your platform would connect with our payment processing APIs. Could you provide technical documentation on your integration capabilities?";
      case "Doordash":
        return "Following our discussion last month, we're ready to move forward with a product demo. Our team would like to see how your platform handles delivery partner communications specifically.";
      case "Robinhood":
        return "The presentation you gave was helpful. We need to discuss how your sales engagement platform would integrate with our existing compliance workflows. Can we schedule a follow-up call?";
      default:
        return `We need more information about how your platform can help with our ${task.task_type.toLowerCase()} workflow. Can you provide details specific to our use case?`;
    }
  };
  
  // Create a suggested response based on the task
  const getSuggestedResponse = () => {
    const contact = task.deal === "Coinbase" ? "Brian" : 
                   task.deal === "Airbnb" ? "Sarah" :
                   task.deal === "Stripe" ? "Michael" :
                   task.deal === "Doordash" ? "Rachel" : "there";
    
    if (task.task_type === "Email") {
      return `Hi ${contact},<br/><br/>
        
        Thank you for your interest in Lysto. I'm excited about the possibility of helping ${task.deal} improve its ${task.task_subtype.toLowerCase() === "initial" ? "sales processes" : "workflow"}.<br/><br/>
        
        ${task.deal === "Coinbase" ? 
          "Regarding crypto payment platform integrations, we offer:<br/>• Direct API integration with major crypto payment processors<br/>• Automated transaction tracking and reconciliation<br/>• Custom workflow triggers based on blockchain events<br/>• Detailed analytics on crypto payment conversions" : 
          task.deal === "Airbnb" ? 
          "Regarding enterprise pricing for 500+ seats, we offer:<br/>• Volume discounts starting at 20% for your size<br/>• Custom onboarding and training packages<br/>• Dedicated customer success manager<br/>• Priority support with 2-hour response time" :
          task.deal === "Stripe" ?
          "Regarding our integration capabilities:<br/>• RESTful APIs with comprehensive documentation<br/>• Webhook support for real-time event handling<br/>• OAuth 2.0 authentication for secure access<br/>• SDK packages for major languages including Node.js, Python, and Ruby" :
          "Here are some key features that address your needs:<br/>• Automated workflow tracking<br/>• Custom notification rules<br/>• Integration with your existing tools<br/>• Advanced reporting and analytics"
        }<br/><br/>
        
        I'd love to schedule a ${task.task_subtype.toLowerCase() === "follow-up" ? "follow-up call" : "technical deep dive"} with your team to explore these options in detail. Would you be available next Tuesday or Wednesday for a 30-minute call?<br/><br/>
        
        Best regards,<br/>
        ${selectedSender.name}`;
    } else if (task.task_type === "Calendar") {
      return `Hi ${contact},<br/><br/>
        
        I wanted to follow up on our previous conversation about ${task.task}.<br/><br/>`;
    }
  };
  
  return (
    <Box
      ref={responseRef}
      p={isMobile ? 3 : 4}
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      width="100%"
      // Use isVisible to gradually fade the component in
      opacity={isVisible ? 1 : 0.7}
      transition="opacity 0.3s ease-in-out"
    >
      {/* Email header with subject and recipient */}
      <VStack spacing={3} align="stretch">
        <Box pb={2} borderBottomWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center">
            <Text color={textColor} fontWeight="medium">
              {emailSubject}
            </Text>
            <Badge colorScheme="blue" px={2} py={1} borderRadius="md">
              Draft
            </Badge>
          </Flex>
        </Box>
        
        {/* Sender/recipient information */}
        <Flex direction="column" gap={1}>
          <Flex>
            <Text width="60px" fontSize="sm" color={secondaryTextColor}>From:</Text>
            <Text fontSize="sm" color={textColor}>{selectedSender.name} &lt;{selectedSender.email}&gt;</Text>
          </Flex>
          <Flex>
            <Text width="60px" fontSize="sm" color={secondaryTextColor}>To:</Text>
            <Text fontSize="sm" color={textColor}>
              {getRecipients()[0].name} &lt;{getRecipients()[0].email}&gt;
            </Text>
          </Flex>
        </Flex>
        
        {/* Email content area */}
        <Box
          mt={2}
          p={3}
          bg={highlightBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
          position="relative"
        >
          {/* Original message */}
          <Box mb={4} fontSize="sm" color={secondaryTextColor}>
            <Text fontStyle="italic" mb={2}>Original message:</Text>
            <Text>{getEmailContext()}</Text>
          </Box>
          
          {/* Divider */}
          <Divider my={3} borderColor={borderColor} />
          
          {/* AI suggested response */}
          <Box>
            <Flex align="center" mb={2}>
              <Icon as={FaMagic} color="blue.500" mr={2} />
              <Text fontSize="sm" fontWeight="medium" color="blue.500">
                AI Suggested Response
              </Text>
            </Flex>
            
            <Box
              fontSize="sm"
              color={textColor}
              dangerouslySetInnerHTML={{ __html: getSuggestedResponse() }}
            />
          </Box>
        </Box>
        
        {/* Sender options dropdown (hidden) */}
        <Box display="none">
          {senderOptions.map((option, index) => (
            <Box 
              key={index} 
              onClick={() => setSelectedSender(option)}
              p={2}
              borderRadius="md"
              _hover={{ bg: tagBg }}
              cursor="pointer"
            >
              {option.name}
            </Box>
          ))}
        </Box>
      </VStack>
    </Box>
  );
};

// Smart features grid similar to HandsFreeFeatures
const SmartEngagementFeatures = ({ onFeatureClick }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  return (
    <SimpleGrid 
      columns={{ base: 2, md: 2, lg: 4 }} 
      spacing={{ base: 3, md: 6 }}
      // Use isMobile to add conditional styling
      px={isMobile ? 2 : 0}
    >
      <GridItem onClick={() => onFeatureClick()}>
        <FeatureCard
          title="Automated Follow-up Prompts"
          description="Never forget to follow up with AI-powered reminders that suggest when and how to reconnect with prospects."
          icon={FaRegBell}
          color="blue"
        />
      </GridItem>
      
      <GridItem onClick={() => onFeatureClick()}>
        <FeatureCard
          title="Reply Tracking"
          description="Automatically track email opens, replies, and engagement so you know exactly where every deal stands."
          icon={FaRegEnvelope}
          color="green"
        />
      </GridItem>
      
      <GridItem onClick={() => onFeatureClick()}>
        <FeatureCard
          title="Meeting Follow-up Notifications"
          description="Get reminders to send notes, action items, and next steps after every meeting to keep deals moving forward."
          icon={FaRegCalendarCheck}
          color="orange"
        />
      </GridItem>
      
      <GridItem onClick={() => onFeatureClick()}>
        <FeatureCard
          title="Prioritized Inbox"
          description="Focus on what matters with AI-prioritized communications and alerts when deals need attention."
          icon={FaRegClock}
          color="red"
        />
      </GridItem>
    </SimpleGrid>
  );
};

// Smart inbox demo component
const SmartInboxDemo = ({ onTaskSelect }) => {
  // Add state for tracking visibility
  const [isVisible, setIsVisible] = useState(false);
  const demoRef = useRef(null);

  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // trigger when at least 10% of the element is visible
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (demoRef.current) {
      observer.observe(demoRef.current);
    }
    
    return () => {
      if (demoRef.current) {
        observer.unobserve(demoRef.current);
      }
    };
  }, []);

  // Colors and theme values
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const selectedBg = useColorModeValue("blue.500", "blue.300");
  const selectedTextColor = useColorModeValue("white", "gray.800");
  const unselectedBg = useColorModeValue("white", "gray.600");
  const unselectedTextColor = useColorModeValue("gray.700", "gray.200");
  
  // State for component
  const [selectedSegment, setSelectedSegment] = useState("my_tasks");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [taskTypeFilter, setTaskTypeFilter] = useState([]);
  const [taskSubtypeFilter, setTaskSubtypeFilter] = useState([]);
  
  // Items per page
  const ITEMS_PER_PAGE = useBreakpointValue({ base: 3, md: 5 });
  
  // Segment options
  const segmentOptions = [
    { label: "My Tasks", value: "my_tasks", tooltip: "Show tasks assigned to your user ID with status 'to do'" },
    { label: "Team Tasks", value: "all_tasks", tooltip: "Show all tasks with status 'to do' regardless of user" },
    { label: "Completed", value: "completed", tooltip: "Show all completed tasks" },
  ];

  // Icons for task types
  const taskTypeIcons = {
    "Reply": FaPaperPlane,
    "Meeting": FaCalendarAlt,
    "Task": FaCheckCircle
  };
  
  // Colors for priority
  const priorityColors = {
    "high": "red",
    "medium": "orange",
    "low": "green"
  };
  
  // Owner colors - consistent colors for each owner
  const ownerColors = {
    "Ricky Roma": { bg: "blue.100", color: "blue.800" },
    "Shelley Levene": { bg: "green.100", color: "green.800" },
    "Dave Moss": { bg: "purple.100", color: "purple.800" },
    "George Aaronow": { bg: "orange.100", color: "orange.800" },
    "John Williamson": { bg: "pink.100", color: "pink.800" },
    "Blake": { bg: "teal.100", color: "teal.800" },
    "Detective Baylen": { bg: "yellow.100", color: "yellow.800" }
  };
  
  // Sample follow-up tasks with tech company IPO data (page 1)
  const allTasksReordered = [
    // Team member tasks that will show first in Team Tasks view
    {
      id: 16,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Initial",
      task: "Send proposal to Stripe integration team",
      deal: "Stripe",
      recipients: "S, J",
      lastActivity: "1 day ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Shelley Levene"
    },
    {
      id: 17,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Schedule",
      task: "Arrange follow-up with Netflix engineering",
      deal: "Netflix",
      recipients: "N, T",
      lastActivity: "2 days ago",
      isUnread: false,
      priority: "medium",
      status: "to do",
      owner: "Dave Moss"
    },
    {
      id: 18,
      type: "Task",
      task_type: "Task",
      task_subtype: "Document",
      task: "Update Shopify case study with new metrics",
      deal: "Shopify",
      recipients: "S",
      lastActivity: "3 days ago",
      isUnread: true,
      priority: "medium",
      status: "to do",
      owner: "George Aaronow"
    },
    {
      id: 19,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Follow-up",
      task: "Check in with Instacart on implementation timeline",
      deal: "Instacart",
      recipients: "I",
      lastActivity: "5 days ago",
      isUnread: false,
      priority: "low",
      status: "to do",
      owner: "John Williamson"
    },
    {
      id: 20,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Preparation",
      task: "Prepare demo for Slack enterprise team",
      deal: "Slack",
      recipients: "S, L",
      lastActivity: "1 week ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Blake"
    },
    {
      id: 21,
      type: "Task",
      task_type: "Task",
      task_subtype: "Compliance",
      task: "Complete compliance review for Zoom contract",
      deal: "Zoom",
      recipients: "Z, B",
      lastActivity: "3 days ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Detective Baylen"
    },
    {
      id: 22,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Client",
      task: "Schedule onboarding call with Twilio team",
      deal: "Twilio",
      recipients: "T, W",
      lastActivity: "4 days ago",
      isUnread: false,
      priority: "medium",
      status: "to do",
      owner: "James Lingk"
    },
    
    // The rest of the active tasks will come next in Team Tasks view
    {
      id: 1,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Reply",
      task: "Reply to: Sarah from Airbnb regarding enterprise pricing",
      deal: "Airbnb",
      recipients: "S",
      lastActivity: "2 days ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 2,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Follow-up",
      task: "Send meeting notes to Robinhood team",
      deal: "Robinhood",
      recipients: "V, T",
      lastActivity: "1 day ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 3,
      type: "Task",
      task_type: "Task",
      task_subtype: "Proposal",
      task: "Prepare proposal for Snowflake integration",
      deal: "Snowflake",
      recipients: "F, J",
      lastActivity: "3 days ago",
      isUnread: false,
      priority: "medium",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 4,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Schedule",
      task: "Schedule demo with Doordash product team",
      deal: "Doordash",
      recipients: "R",
      lastActivity: "5 days ago",
      isUnread: false,
      priority: "medium",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 5,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Follow-up",
      task: "Follow up with CTO at UiPath",
      deal: "UiPath",
      recipients: "D",
      lastActivity: "1 week ago",
      isUnread: true,
      priority: "medium",
      status: "to do",
      owner: "Ricky Roma"
    },
    
    // Page 2 tasks
    {
      id: 6,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Initial",
      task: "Respond to Coinbase partnership inquiry",
      deal: "Coinbase",
      recipients: "B",
      lastActivity: "2 weeks ago",
      isUnread: false,
      priority: "low",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 7,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Schedule",
      task: "Set up quarterly review with Spotify team",
      deal: "Spotify",
      recipients: "M, L",
      lastActivity: "3 days ago",
      isUnread: true,
      priority: "high",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 8,
      type: "Task",
      task_type: "Task",
      task_subtype: "Research",
      task: "Research integration capabilities for Pinterest",
      deal: "Pinterest",
      recipients: "P",
      lastActivity: "1 week ago",
      isUnread: false,
      priority: "medium",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 9,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Follow-up",
      task: "Send product roadmap to Roblox team",
      deal: "Roblox",
      recipients: "R, O",
      lastActivity: "4 days ago",
      isUnread: true,
      priority: "medium",
      status: "to do",
      owner: "Ricky Roma"
    },
    {
      id: 10,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Preparation",
      task: "Prepare slides for Palantir demo",
      deal: "Palantir",
      recipients: "J",
      lastActivity: "6 days ago",
      isUnread: false,
      priority: "low",
      status: "to do",
      owner: "Ricky Roma"
    },
    
    // Completed tasks
    {
      id: 11,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Reply",
      task: "Reply to: Product feedback from CrowdStrike",
      deal: "CrowdStrike",
      recipients: "C",
      lastActivity: "2 days ago",
      isUnread: false,
      priority: "medium",
      status: "completed",
      completedDate: "Yesterday",
      owner: "Ricky Roma"
    },
    {
      id: 12,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Follow-up",
      task: "Send contract details to Square",
      deal: "Square",
      recipients: "S, Q",
      lastActivity: "1 week ago",
      isUnread: false,
      priority: "high",
      status: "completed",
      completedDate: "3 days ago",
      owner: "Ricky Roma"
    },
    {
      id: 13,
      type: "Task",
      task_type: "Task",
      task_subtype: "Document",
      task: "Prepare case study for Etsy integration",
      deal: "Etsy",
      recipients: "E",
      lastActivity: "2 weeks ago",
      isUnread: false,
      priority: "medium",
      status: "completed",
      completedDate: "Last week",
      owner: "Ricky Roma"
    },
    {
      id: 14,
      type: "Meeting",
      task_type: "Calendar",
      task_subtype: "Preparation",
      task: "Prepare demo for Zoom team",
      deal: "Zoom",
      recipients: "Z",
      lastActivity: "3 weeks ago",
      isUnread: false,
      priority: "medium",
      status: "completed",
      completedDate: "2 weeks ago",
      owner: "Ricky Roma"
    },
    {
      id: 15,
      type: "Reply",
      task_type: "Email",
      task_subtype: "Initial",
      task: "Send welcome email to Datadog",
      deal: "Datadog",
      recipients: "D",
      lastActivity: "1 month ago",
      isUnread: false,
      priority: "low",
      status: "completed",
      completedDate: "3 weeks ago",
      owner: "Ricky Roma"
    }
  ];
  
  // Replace the allTasks array with the reordered one
  const allTasks = allTasksReordered;
  
  // Search filter function
  const searchFilter = (task) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    return (
      task.task.toLowerCase().includes(query) ||
      task.deal.toLowerCase().includes(query) ||
      task.task_type.toLowerCase().includes(query) ||
      task.task_subtype.toLowerCase().includes(query)
    );
  };
  
  // Apply task type filter
  const taskTypeFilterFn = (task) => {
    if (taskTypeFilter.length === 0) return true;
    return taskTypeFilter.includes(task.task_type.toLowerCase());
  };
  
  // Apply task subtype filter
  const subtypeFilterFn = (task) => {
    if (taskSubtypeFilter.length === 0) return true;
    return taskSubtypeFilter.includes(task.task_subtype.toLowerCase());
  };
  
  // Get filtered tasks based on selected segment and filters
  const filteredTasks = allTasks.filter(task => {
    // First filter by segment/status
    if (selectedSegment === "completed") {
      if (task.status !== "completed") return false;
    } else if (task.status !== "to do") {
      return false;
    }
    
    // Then by owner for my_tasks
    if (selectedSegment === "my_tasks" && task.owner !== "Ricky Roma") {
      return false;
    }
    
    // Then by search and other filters
    return (
      searchFilter(task) && 
      taskTypeFilterFn(task) && 
      subtypeFilterFn(task)
    );
  });
  
  // Get paginated tasks
  const totalTasks = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalTasks / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTasks = filteredTasks.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };
  
  const handlePageSelect = (pageNum) => {
    setCurrentPage(pageNum);
  };
  
  // Get unique task types and subtypes for filter options
  const taskTypes = [...new Set(allTasks.map(item => item.task_type?.toLowerCase()))];
  const taskSubtypes = [...new Set(allTasks.map(item => item.task_subtype?.toLowerCase()))];
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'taskType') {
      setTaskTypeFilter(value);
    } else if (filterType === 'taskSubtype') {
      setTaskSubtypeFilter(value);
    }
    
    // Reset to first page when filters change
    setCurrentPage(1);
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    setTaskTypeFilter([]);
    setTaskSubtypeFilter([]);
    setCurrentPage(1);
  };
  
  // Function to generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(1);
    
    // For a small number of pages, show all pages without ellipsis
    if (totalPages <= 7) {
      for (let i = 2; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
      return pageNumbers;
    }
    
    // For more pages, show intelligent pagination with ellipsis
    
    // Calculate range around current page
    const showEllipsisStart = currentPage > 3;
    const showEllipsisEnd = currentPage < totalPages - 2;
    
    if (showEllipsisStart) {
      // Show ellipsis after first page
      pageNumbers.push('...');
    } else {
      // Otherwise show pages 2 and 3
      if (totalPages >= 2) pageNumbers.push(2);
      if (totalPages >= 3) pageNumbers.push(3);
    }
    
    // Always show pages around current page
    if (currentPage > 3 && currentPage < totalPages - 2) {
      pageNumbers.push(currentPage - 1);
      pageNumbers.push(currentPage);
      pageNumbers.push(currentPage + 1);
    }
    
    if (showEllipsisEnd) {
      // Show ellipsis before last page
      pageNumbers.push('...');
    } else {
      // Otherwise show second and third last pages
      if (totalPages >= 4) pageNumbers.push(totalPages - 2);
      if (totalPages >= 3) pageNumbers.push(totalPages - 1);
    }
    
    // Always show last page, unless it's already been added
    if (totalPages > 1 && pageNumbers[pageNumbers.length - 1] !== totalPages) {
      pageNumbers.push(totalPages);
    }
    
    // Remove duplicates that might occur at boundaries
    return [...new Set(pageNumbers)];
  };
  
  // Handle segment change
  const handleSegmentChange = (segment) => {
    setSelectedSegment(segment);
    setCurrentPage(1); // Reset to first page
  };
  
  // Handle task click
  const handleTaskClick = (task) => {
    if (onTaskSelect) {
      onTaskSelect(task);
    }
  };
  
  return (
    <Box
      ref={demoRef}
      bg={bgColor}
      borderRadius="lg"
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      maxW={{ base: "100%", md: "100%" }}
      mx="auto"
      className={isVisible ? 'animation-active' : 'animation-paused'}
    >
      {/* Controls Container */}
      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 4, md: 6 }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        w="100%"
        p={5}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={useColorModeValue("white", "gray.700")}
      >
        {/* Segment Options */}
        <Flex gap={4} align="center">
          <ButtonGroup isAttached variant="outline" spacing={0}>
            {segmentOptions.map((option) => {
              const isSelected = selectedSegment === option.value;
              
              return (
                <Button
                  key={option.value}
                  onClick={() => handleSegmentChange(option.value)}
                  bg={isSelected ? selectedBg : unselectedBg}
                  color={isSelected ? selectedTextColor : unselectedTextColor}
                  _hover={{
                    bg: isSelected ? selectedBg : hoverBg,
                  }}
                  borderRadius="md"
                  minW="100px"
                  borderWidth="1px"
                  borderColor={borderColor}
                  size="md"
                  fontWeight="bold"
                >
                  {option.label}
                </Button>
              );
            })}
          </ButtonGroup>
          
          {/* Filter Button with Popover - hidden on mobile */}
          <Box display={{ base: "none", md: "block" }}>
            <Popover
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              placement="bottom-start"
              closeOnBlur={true}
            >
              <PopoverTrigger>
                <Button
                  leftIcon={<Icon as={FiFilter} />}
                  rightIcon={<Icon as={FaChevronDown} />}
                  variant="outline"
                  size="md"
                  borderRadius="md"
                  w={{ base: "full", sm: "auto" }}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  bg="white"
                >
                  {taskTypeFilter.length > 0 || taskSubtypeFilter.length > 0 ? 
                    `Filters (${taskTypeFilter.length + taskSubtypeFilter.length})` : 
                    'Filters'}
                </Button>
              </PopoverTrigger>
              <PopoverContent width="300px" p={0}>
                <Box p={4} borderBottomWidth="1px" borderColor={borderColor}>
                  <Heading size="sm" mb={3}>Task Filters</Heading>
                  
                  {/* Task Type Filter */}
                  <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Task Type</Text>
                    <VStack align="start" spacing={1}>
                      {taskTypes.map(type => (
                        <Checkbox 
                          key={type} 
                          value={type}
                          isChecked={taskTypeFilter.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleFilterChange('taskType', [...taskTypeFilter, type]);
                            } else {
                              handleFilterChange('taskType', 
                                taskTypeFilter.filter(t => t !== type)
                              );
                            }
                          }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </Checkbox>
                      ))}
                    </VStack>
                  </Box>
                  
                  {/* Task Subtype Filter */}
                  <Box mb={4}>
                    <Text fontWeight="medium" mb={2}>Task Subtype</Text>
                    <VStack align="start" spacing={1} maxH="150px" overflowY="auto">
                      {taskSubtypes.map(subtype => (
                        <Checkbox 
                          key={subtype} 
                          value={subtype}
                          isChecked={taskSubtypeFilter.includes(subtype)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleFilterChange('taskSubtype', [...taskSubtypeFilter, subtype]);
                            } else {
                              handleFilterChange('taskSubtype', 
                                taskSubtypeFilter.filter(t => t !== subtype)
                              );
                            }
                          }}
                        >
                          {subtype.charAt(0).toUpperCase() + subtype.slice(1)}
                        </Checkbox>
                      ))}
                    </VStack>
                  </Box>
                </Box>
                
                <Flex p={3} borderTopWidth="1px" borderColor={borderColor} justify="flex-end">
                  <Button 
                    variant="ghost" 
                    mr={2} 
                    onClick={handleResetFilters}
                    isDisabled={taskTypeFilter.length === 0 && taskSubtypeFilter.length === 0}
                  >
                    Reset
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Apply
                  </Button>
                </Flex>
              </PopoverContent>
            </Popover>
          </Box>
        </Flex>
        
        {/* Search */}
        <Flex gap={4} align="center" justify={{ base: "flex-start", md: "flex-end" }}>
          <Box w={{ base: "100%", md: "280px" }}>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Search inbox..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                bg={unselectedBg}
                borderColor={borderColor}
                _hover={{ borderColor: "gray.400" }}
                _focus={{ borderColor: "blue.500", boxShadow: "none" }}
                size="md"
                fontSize="md"
              />
            </InputGroup>
          </Box>
        </Flex>
      </Flex>
      
      {/* Table Header - Desktop */}
      <Grid
        templateColumns="minmax(250px, 1fr) 150px 180px 100px 120px"
        gap={8}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={useColorModeValue("gray.50", "gray.700")}
        px={8}
        py={3}
        display={{ base: "none", lg: "grid" }}
      >
        <Text fontSize="xs" fontWeight="semibold" color={textColor} letterSpacing="0.5px" textTransform="uppercase">Task</Text>
        <Text fontSize="xs" fontWeight="semibold" color={textColor} letterSpacing="0.5px" textTransform="uppercase">Owner</Text>
        <Text fontSize="xs" fontWeight="semibold" color={textColor} letterSpacing="0.5px" textTransform="uppercase">Company</Text>
        <Text fontSize="xs" fontWeight="semibold" color={textColor} letterSpacing="0.5px" textTransform="uppercase">Recipients</Text>
        <Text fontSize="xs" fontWeight="semibold" color={textColor} letterSpacing="0.5px" textTransform="uppercase">
          {selectedSegment === "completed" ? "Completed" : "Last Activity"}
        </Text>
      </Grid>
      
      {/* Mobile List View */}
      <Box display={{ base: "block", lg: "none" }}>
        {paginatedTasks.length > 0 ? (
          paginatedTasks.map((task) => (
            <Box
              key={task.id}
              className="mobile-task-item"
              data-task-id={task.id}
              borderBottomWidth="1px"
              borderColor={borderColor}
              px={5}
              py={4}
              _hover={{ bg: hoverBg }}
              transition="background 0.2s"
              cursor="pointer"
              position="relative"
              overflow="hidden"
              onClick={() => handleTaskClick(task)}
            >
              {/* Unread indicator */}
              {task.isUnread && (
                <Box 
                  position="absolute" 
                  top={0} 
                  left={0} 
                  w="4px" 
                  h="100%" 
                  bg={`${priorityColors[task.priority]}.500`} 
                />
              )}
              
              <Flex alignItems="center" mb={3}>
                <Center
                  bg={`${priorityColors[task.priority]}.50`}
                  color={`${priorityColors[task.priority]}.500`}
                  borderRadius="md"
                  p={2}
                  boxSize="34px"
                  mr={3}
                >
                  <Icon 
                    as={taskTypeIcons[task.type]} 
                    boxSize="16px"
                  />
                </Center>
                <Text 
                  fontSize="md" 
                  fontWeight={task.isUnread ? "bold" : "medium"}
                  color={textColor}
                  noOfLines={1}
                >
                  {task.task}
                </Text>
              </Flex>
              
              <Flex justify="space-between" align="center" mb={2}>
                <Flex align="center">
                  <Avatar 
                    size="sm" 
                    mr={2} 
                    name={task.deal} 
                    bg={`${priorityColors[task.priority]}.100`} 
                    color={`${priorityColors[task.priority]}.800`} 
                  />
                  <Text fontSize="sm" color={secondaryTextColor} noOfLines={1}>
                    {task.deal}
                  </Text>
                </Flex>
                
                <Text fontSize="sm" color={secondaryTextColor}>
                  {selectedSegment === "completed" ? task.completedDate : task.lastActivity}
                </Text>
              </Flex>
              
              <Flex justify="space-between" align="center">
                <Flex align="center">
                  <Avatar 
                    size="xs" 
                    mr={2} 
                    name={task.owner} 
                    bg={ownerColors[task.owner]?.bg || "gray.100"}
                    color={ownerColors[task.owner]?.color || "gray.800"}
                  />
                  <Text fontSize="xs" color={secondaryTextColor} noOfLines={1}>
                    {task.owner}
                  </Text>
                </Flex>
              </Flex>
            </Box>
          ))
        ) : (
          <Box py={10} textAlign="center" color={secondaryTextColor}>
            <Icon as={MdInbox} boxSize="40px" mx="auto" mb={4} color="gray.400" />
            <Text fontSize="lg">No tasks found.</Text>
            <Text fontSize="md">
              {searchQuery ? "Try a different search term" : "Check back later for new tasks"}
            </Text>
          </Box>
        )}
      </Box>
      
      {/* Desktop Table Rows */}
      <Box display={{ base: "none", lg: "block" }}>
        {paginatedTasks.length > 0 ? (
          paginatedTasks.map((task) => (
            <Grid
              key={task.id}
              className="desktop-task-item"
              data-task-id={task.id}
              templateColumns="minmax(250px, 1fr) 150px 180px 100px 120px"
              gap={8}
              borderBottomWidth="1px"
              borderColor={borderColor}
              px={8}
              py={4}
              _hover={{ bg: hoverBg }}
              transition="background 0.2s"
              cursor="pointer"
              position="relative"
              overflow="hidden"
              onClick={() => handleTaskClick(task)}
            >
              {/* Unread indicator */}
              {task.isUnread && (
                <Box 
                  position="absolute" 
                  top={0} 
                  left={0} 
                  w="4px" 
                  h="100%" 
                  bg={`${priorityColors[task.priority]}.500`} 
                />
              )}
              
              <Flex alignItems="center">
                <Center
                  bg={`${priorityColors[task.priority]}.50`}
                  color={`${priorityColors[task.priority]}.500`}
                  borderRadius="md"
                  p={2}
                  boxSize="38px"
                  mr={3}
                >
                  <Icon 
                    as={taskTypeIcons[task.type]} 
                    boxSize="18px"
                  />
                </Center>
                <Box>
                  <Text 
                    fontSize="md" 
                    fontWeight={task.isUnread ? "bold" : "medium"}
                    color={textColor}
                    noOfLines={1}
                  >
                    {task.task}
                  </Text>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    {task.task_type} • {task.task_subtype}
                  </Text>
                </Box>
              </Flex>
              
              <Flex alignItems="center">
                <Avatar 
                  size="sm" 
                  name={task.owner} 
                  bg={ownerColors[task.owner]?.bg || "gray.100"}
                  color={ownerColors[task.owner]?.color || "gray.800"}
                  fontSize="12px"
                  mr={2}
                />
                <Text fontSize="sm" color={textColor} fontWeight="medium" noOfLines={1}>
                  {task.owner}
                </Text>
              </Flex>
              
              <Flex alignItems="center">
                <Avatar 
                  size="sm" 
                  mr={3} 
                  name={task.deal} 
                  bg={`${priorityColors[task.priority]}.100`} 
                  color={`${priorityColors[task.priority]}.800`} 
                />
                <Text fontSize="sm" color={textColor} fontWeight="medium" noOfLines={1}>
                  {task.deal}
                </Text>
              </Flex>
              
              <Flex>
                {task.recipients.split(", ").map((r, i) => (
                  <Avatar 
                    key={i} 
                    size="sm" 
                    name={r} 
                    bg={
                      ["blue.100", "green.100", "orange.100", "purple.100", "red.100"][i % 5]
                    }
                    color={
                      ["blue.800", "green.800", "orange.800", "purple.800", "red.800"][i % 5]
                    }
                    fontSize="12px"
                    mr={1}
                  />
                ))}
              </Flex>
              
              <Flex alignItems="center">
                <Icon as={selectedSegment === "completed" ? FaCheckCircle : FaClock} 
                  color={secondaryTextColor} 
                  mr={2} 
                  boxSize="16px" 
                />
                <Text fontSize="md" color={secondaryTextColor}>
                  {selectedSegment === "completed" ? task.completedDate : task.lastActivity}
                </Text>
              </Flex>
            </Grid>
          ))
        ) : (
          <Box py={16} textAlign="center" color={secondaryTextColor}>
            <Icon as={MdInbox} boxSize="60px" mx="auto" mb={4} color="gray.400" />
            <Text fontSize="xl">No tasks found.</Text>
            <Text fontSize="lg">
              {searchQuery ? "Try a different search term" : "Check back later for new tasks"}
            </Text>
          </Box>
        )}
      </Box>
      
      {/* Pagination - Similar to PaginationControls.jsx */}
      {totalTasks > 0 && (
        <Flex 
          p={5} 
          borderTopWidth="1px" 
          borderColor={borderColor}
          justify="center"
          align="center"
          flexWrap="wrap"
          gap={2}
        >
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<Icon as={FaChevronLeft} boxSize={4} />}
              onClick={handlePreviousPage}
              isDisabled={currentPage === 1}
              variant="ghost"
              aria-label="Previous page"
              colorScheme="gray"
              size="md"
              height="40px"
              borderRadius="md"
            />
            
            {getPageNumbers().map((pageNum, index) => (
              pageNum === '...' ? (
                <Text key={`ellipsis-${index}`} color="gray.500" px={2}>
                  {pageNum}
                </Text>
              ) : (
                <Button
                  key={pageNum}
                  onClick={() => handlePageSelect(pageNum)}
                  variant={currentPage === pageNum ? 'solid' : 'ghost'}
                  colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                  size="md"
                  height="40px"
                  minW="40px"
                  borderRadius="xl"
                >
                  {pageNum}
                </Button>
              )
            ))}
            
            <IconButton
              icon={<Icon as={FaChevronRight} boxSize={4} />}
              onClick={handleNextPage}
              isDisabled={currentPage >= totalPages}
              variant="ghost"
              aria-label="Next page"
              colorScheme="gray"
              size="md"
              height="40px"
              borderRadius="md"
            />
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

// Animation definitions for tab switching
const MotionBox = motion(Box);
const slideVariants = {
  hidden: (direction) => ({ 
    x: direction === 'right' ? '100%' : '-100%',
    opacity: 0
  }),
  visible: { 
    x: 0,
    opacity: 1,
    transition: { type: 'easeInOut', duration: 0.4 }
  },
  exit: (direction) => ({ 
    x: direction === 'right' ? '-100%' : '100%',
    opacity: 0,
    transition: { type: 'easeInOut', duration: 0.4 }
  }),
  // Add a special animation for the inbox panel exit
  inboxExit: {
    opacity: 0,
    x: '-100%',
    transition: { type: 'easeInOut', duration: 0.8 }
  },
  // Add a special animation for the engage panel exit when going back
  engageExit: {
    opacity: 0,
    x: '100%',
    transition: { type: 'easeInOut', duration: 0.6 }
  },
  // Add a special animation for the inbox panel enter when going back
  inboxEnter: {
    opacity: 1,
    x: 0,
    scale: [0.98, 1.02, 1],
    transition: { 
      type: 'spring', 
      stiffness: 300, 
      damping: 20,
      duration: 0.8
    }
  }
};

const LandingInboxSection = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const tabHoverBg = useColorModeValue("gray.100", "gray.700");
  
  // Ref for the demo section for scrolling
  const demoSectionRef = React.useRef(null);
  
  // State to track which tab is active and which task is selected
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [animationDirection, setAnimationDirection] = useState('right');
  const [showTooltip, setShowTooltip] = useState(true);
  
  // Close the tooltip after a delay
  React.useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);
  
  // Handler to switch to Response tab when a task is clicked
  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setAnimationDirection('right');
    
    // Animate the entire panel
    const inboxPanel = document.getElementById('inbox-panel');
    if (inboxPanel) {
      // Use class-based animation instead of direct style manipulation
      inboxPanel.classList.add('panel-exit');
    }
    
    // Delay tab change to let animation complete
    setTimeout(() => {
      setTabIndex(1); // Switch to Engage Assist tab
    }, 800);
  };
  
  // Handle tab changes with correct animation direction
  const handleTabChange = (index) => {
    // If going from Engage Assist back to Inbox
    if (index === 0 && tabIndex === 1) {
      // Mark the tab to add a visual indicator
      const tabElement = document.querySelector('.smart-inbox-tab');
      if (tabElement) {
        tabElement.style.animation = `${activeTabIndicatorAnimation} 0.6s forwards`;
      }
      
      // Animate the engage panel out
      const engagePanel = document.getElementById('engage-panel');
      if (engagePanel) {
        // First, add a class for CSS-based animation
        engagePanel.classList.add('panel-reverse-exit');
        
        // Also set the animation directly for enhanced reliability
        engagePanel.style.animation = `${panelReverseExitAnimation} 0.6s forwards`;
        
        // Then set animationDirection for the motion components
        setAnimationDirection('left');
      }
      
      // Reset the inbox panel state and animate it in
      setTimeout(() => {
        // Update the tab index after a short delay for visual effect
        setTabIndex(0);
        
        // After tab change, ensure inbox panel is visible and ready to animate in
        setTimeout(() => {
          const inboxPanel = document.getElementById('inbox-panel');
          if (inboxPanel) {
            // Clear any previous animations/classes
            inboxPanel.classList.remove('panel-exit');
            
            // Add an entrance class
            inboxPanel.classList.add('panel-reverse-enter');
            
            // Also set the animation directly
            inboxPanel.style.animation = `${panelReverseEnterAnimation} 0.8s forwards`;
            inboxPanel.style.opacity = '1';
            inboxPanel.style.transform = 'translateX(0)';
            
            // Add a subtle scale effect for visual appeal
            inboxPanel.style.transition = 'all 0.5s';
            inboxPanel.style.transform = 'scale(1)';
          }
        }, 100);
      }, 400); // Slightly longer delay for a better sequenced animation
    } 
    // Going from Inbox to Engage Assist (handled by handleTaskSelect)
    else if (index === 1 && tabIndex === 0) {
      // Instead of blocking the tab change, let's simulate clicking a task
      // This creates a better experience when clicking directly on the tab
      if (!selectedTask) {
        // Use a default task or the first one in the list
        const defaultTask = {
          id: 0,
          type: "Reply",
          task_type: "Email",
          task_subtype: "Reply",
          task: "Continue your conversation",
          deal: "Demo Company",
          priority: "medium",
        };
        
        // Call handleTaskSelect directly
        handleTaskSelect(defaultTask);
      } else {
        // If there's already a selected task, just show the Engage Assist tab
        setAnimationDirection('right');
        setTabIndex(1);
      }
    }
    // Any other tab changes
    else {
      if (index > tabIndex) {
        setAnimationDirection('right');
      } else {
        setAnimationDirection('left');
      }
      setTabIndex(index);
    }
  };

  // Update the back button click handler to provide visual feedback
  const handleBackButtonClick = () => {
    // Animate the button itself for feedback
    const backButton = document.querySelector('.back-to-inbox-button');
    if (backButton) {
      backButton.style.animation = `${backButtonClickAnimation} 0.4s forwards`;
      
      // Reset animation after it completes
      setTimeout(() => {
        backButton.style.animation = 'none';
      }, 400);
    }
    
    // Call the tab change handler
    handleTabChange(0);
  };

  // Scroll to demo section when feature cards are clicked
  const scrollToDemo = () => {
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box bg={bgColor} py={20}>
      <Container maxW="1400px" px={{ base: 4, md: 8 }}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Headline Section */}
          <VStack spacing={{ base: 3, md: 5 }} align="center" textAlign="center">
            <Flex
              bg="green.50"
              borderRadius="full"
              p={2}
              alignItems="center"
              justifyContent="center"
              boxSize="60px"
              mb={2}
            >
              <Icon as={MdInbox} color="green.500" boxSize={8} />
            </Flex>
            
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color={textColor}
            >
              Smart Engagement Reminders
            </Heading>
            
            <Text
              fontSize={{ base: "md", md: "lg" }}
              maxW="800px"
              color={secondaryTextColor}
            >
              Never let an important follow-up fall through the cracks. Lysto ensures every deal gets the attention it needs, exactly when it needs it.
            </Text>
          </VStack>

          {/* Smart Features Grid */}
          <Box px={{ base: 2, md: 0 }} py={{ base: 4, md: 6 }}>
            <SmartEngagementFeatures onFeatureClick={scrollToDemo} />
          </Box>
          
          {/* Demo Tabs with Interactivity Affordance */}
          <Box position="relative">            
            <Tabs 
              variant="soft-rounded" 
              colorScheme="green" 
              index={tabIndex} 
              onChange={handleTabChange}
            >
              <TabList justifyContent="center" mb={3} flexWrap="wrap" gap={2}>
                <Tab 
                  _hover={{ bg: tabHoverBg }} 
                  mx={{ base: 1, md: 2 }}
                  className="smart-inbox-tab"
                >
                  <HStack spacing={2}>
                    <Icon as={FaInbox} boxSize={4} />
                    <Text>Smart Inbox</Text>
                  </HStack>
                </Tab>
                <Tab 
                  _hover={{ bg: tabHoverBg }} 
                  mx={{ base: 1, md: 2 }}
                  className="engage-assist-tab"
                >
                  <HStack spacing={2}>
                    <Icon as={FaCommentDots} boxSize={4} />
                    <Text>Engage Assist</Text>
                  </HStack>
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <MotionBox
                    id="inbox-panel"
                    key="inbox-panel"
                    custom={animationDirection}
                    variants={slideVariants}
                    initial={tabIndex === 0 ? "visible" : "hidden"}
                    animate={tabIndex === 0 ? (animationDirection === 'left' ? "inboxEnter" : "visible") : "inboxExit"}
                    exit="exit"
                    maxW={{ base: "100%", sm: "100%" }} 
                    mx="auto"
                    sx={{
                      '&.panel-exit': {
                        animation: `${panelExitAnimation} 0.8s forwards`
                      },
                      '&.panel-reverse-enter': {
                        animation: `${panelReverseEnterAnimation} 0.8s forwards`
                      }
                    }}
                    display={tabIndex === 0 ? "block" : "none"}
                  >
                    <SmartInboxDemo onTaskSelect={handleTaskSelect} />
                  </MotionBox>
                </TabPanel>
                <TabPanel px={0}>
                  <MotionBox
                    id="engage-panel"
                    key="response-panel"
                    custom={animationDirection}
                    variants={slideVariants}
                    initial={tabIndex === 1 ? "visible" : "hidden"}
                    animate={tabIndex === 1 ? "visible" : "engageExit"}
                    exit="exit"
                    maxW={{ base: "100%", sm: "100%" }} 
                    mx="auto"
                    display={tabIndex === 1 ? "block" : "none"}
                    sx={{
                      '&.panel-exit': {
                        animation: `${panelExitAnimation} 0.4s forwards`
                      },
                      '&.panel-reverse-exit': {
                        animation: `${panelReverseExitAnimation} 0.6s forwards`
                      }
                    }}
                  >
                    <EmailResponseSuggestion selectedTask={selectedTask} />
                    
                    {/* Add a "Back to Inbox" button with enhanced styling */}
                    <Flex justify="center" mt={4}>
                      <Button 
                        className="back-to-inbox-button"
                        leftIcon={<Icon as={FaArrowRight} transform="rotate(180deg)" />}
                        colorScheme="blue"
                        onClick={handleBackButtonClick}
                        size="md"
                        fontWeight="bold"
                        boxShadow="sm"
                        _hover={{
                          transform: "translateY(-2px)",
                          boxShadow: "md",
                        }}
                        transition="all 0.2s"
                      >
                        Back to Smart Inbox
                      </Button>
                    </Flex>
                  </MotionBox>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default LandingInboxSection; 